import { generateClient } from 'aws-amplify/api';
import { 
  Category,
  Expense,
  User,
  UserRole,
  CreateUserInput,
  UpdateUserInput
} from '../types';
import { GraphQLResponse } from '../types/api';
import { 
  CREATE_CATEGORY,
  UPDATE_CATEGORY,
  DELETE_CATEGORY
} from './graphql/mutations';
import { 
  GET_CATEGORIES,
  GET_ALL_EXPENSES,
  GET_ALL_USERS
} from './graphql/queries';

// Lazy initialize client to avoid "Amplify not configured" error
// `any`, as in leave.ts: the generated type sends TypeScript 5+ (the mobile app) into "excessive
// stack depth" (TS2321), and nothing here relies on it.
let client: any = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};

interface CategoryInput {
  name: string;
  description: string;
  isActive: boolean;
  maxAmount?: number;
  requiresReceipt?: boolean;
}

/**
 * Service for handling admin operations
 * Requirements: 4.1, 4.4 - Finance admin access and category management
 */
export class AdminService {

  static async getCategories(): Promise<Category[]> {
    try {
      const response = await getClient().graphql({
        query: GET_CATEGORIES
      }) as GraphQLResponse<{ listCategories: Category[] }>;

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      return response.data?.listCategories || [];
    } catch (error) {
      console.error('Failed to get categories:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to get categories: ${error.message}` 
          : 'Failed to retrieve categories'
      );
    }
  }

  static async createCategory(input: CategoryInput): Promise<Category> {
    try {
      // Match the AppSync schema CreateCategoryInput (flat structure, no nested limits)
      const categoryInput = {
        name: input.name,
        description: input.description,
        isActive: input.isActive,
        maxAmount: input.maxAmount,
        requiresReceipt: input.requiresReceipt
      };

      const response = await getClient().graphql({
        query: CREATE_CATEGORY,
        variables: { input: categoryInput }
      }) as GraphQLResponse<{ createCategory: Category }>;

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      if (!response.data?.createCategory) {
        throw new Error('Failed to create category');
      }

      return response.data.createCategory;
    } catch (error) {
      console.error('Failed to create category:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to create category: ${error.message}` 
          : 'Failed to create category'
      );
    }
  }

  static async updateCategory(id: string, input: CategoryInput): Promise<Category> {
    try {
      const categoryInput = {
        name: input.name,
        description: input.description,
        isActive: input.isActive,
        limits: {
          maxAmount: input.maxAmount,
          requiresReceipt: input.requiresReceipt
        }
      };

      const response = await getClient().graphql({
        query: UPDATE_CATEGORY,
        variables: { id, input: categoryInput }
      }) as GraphQLResponse<{ updateCategory: Category }>;

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      if (!response.data?.updateCategory) {
        throw new Error('Failed to update category');
      }

      return response.data.updateCategory;
    } catch (error) {
      console.error('Failed to update category:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to update category: ${error.message}` 
          : 'Failed to update category'
      );
    }
  }

  static async deleteCategory(id: string): Promise<void> {
    try {
      const response = await getClient().graphql({
        query: DELETE_CATEGORY,
        variables: { id }
      }) as GraphQLResponse<{ deleteCategory: { success: boolean; message: string } }>;

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      if (!response.data?.deleteCategory?.success) {
        throw new Error(response.data?.deleteCategory?.message || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Failed to delete category:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to delete category: ${error.message}` 
          : 'Failed to delete category'
      );
    }
  }

  static async getAllExpenses(filters?: {
    status?: string[];
    dateFrom?: string;
    dateTo?: string;
    department?: string;
    category?: string[];
  }): Promise<Expense[]> {
    try {
      const response = await getClient().graphql({
        query: GET_ALL_EXPENSES,
        variables: { filters }
      }) as GraphQLResponse<{ getAllExpenses: { items: Expense[] } }>;

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      return response.data?.getAllExpenses?.items || [];
    } catch (error) {
      console.error('Failed to get all expenses:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to get all expenses: ${error.message}` 
          : 'Failed to retrieve all expenses'
      );
    }
  }

  static async getAllUsers(): Promise<User[]> {
    try {
      const response = await getClient().graphql({
        query: GET_ALL_USERS
      }) as GraphQLResponse<{ getAllUsers: User[] }>;

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      return response.data?.getAllUsers || [];
    } catch (error) {
      console.error('Failed to get all users:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to get all users: ${error.message}` 
          : 'Failed to retrieve all users'
      );
    }
  }

  static async updateUserRole(userId: string, newRole: UserRole): Promise<User> {
    try {
      const query = `
        mutation UpdateUserRole($userId: ID!, $role: UserRole!) {
          updateUserRole(userId: $userId, role: $role) {
            id email name employeeId department managerEmail role createdAt updatedAt
          }
        }
      `;
      const response = await getClient().graphql({
        query,
        variables: { userId, role: newRole }
      }) as GraphQLResponse<{ updateUserRole: User }>;

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      if (!response.data?.updateUserRole) {
        throw new Error('Failed to update user role');
      }

      return response.data.updateUserRole;
    } catch (error) {
      console.error('Failed to update user role:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to update user role: ${error.message}` 
          : 'Failed to update user role'
      );
    }
  }

  static async getSystemStatistics(): Promise<{
    totalUsers: number;
    totalExpenses: number;
    totalAmount: number;
    pendingApprovals: number;
    activeCategories: number;
  }> {
    try {
      const query = `
        query GetSystemStatistics {
          getSystemStatistics {
            totalUsers totalExpenses totalAmount pendingApprovals activeCategories
          }
        }
      `;
      const response = await getClient().graphql({ query }) as GraphQLResponse<{ 
        getSystemStatistics: {
          totalUsers: number;
          totalExpenses: number;
          totalAmount: number;
          pendingApprovals: number;
          activeCategories: number;
        }
      }>;

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      return response.data?.getSystemStatistics || {
        totalUsers: 0,
        totalExpenses: 0,
        totalAmount: 0,
        pendingApprovals: 0,
        activeCategories: 0
      };
    } catch (error) {
      console.error('Failed to get system statistics:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to get system statistics: ${error.message}` 
          : 'Failed to retrieve system statistics'
      );
    }
  }

  static hasAdminAccess(user: User | null): boolean {
    return user?.role === UserRole.FINANCE_ADMIN || user?.role === UserRole.SUPER_ADMIN;
  }

  static canAccessAllExpenses(user: User | null): boolean {
    return user?.role === UserRole.FINANCE_ADMIN || user?.role === UserRole.SUPER_ADMIN;
  }

  static canManageUsers(user: User | null): boolean {
    return user?.role === UserRole.SUPER_ADMIN;
  }

  static canManageCategories(user: User | null): boolean {
    return user?.role === UserRole.FINANCE_ADMIN || user?.role === UserRole.SUPER_ADMIN;
  }

  static validateCategoryInput(input: CategoryInput): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!input.name || input.name.trim().length === 0) {
      errors.push('Category name is required');
    }

    if (input.name && input.name.trim().length > 50) {
      errors.push('Category name must be 50 characters or less');
    }

    if (input.description && input.description.length > 200) {
      errors.push('Category description must be 200 characters or less');
    }

    if (input.maxAmount !== undefined && input.maxAmount <= 0) {
      errors.push('Maximum amount must be greater than 0');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  static async updateUserProfile(userId: string, updates: UpdateUserInput): Promise<User> {
    try {
      const query = `
        mutation UpdateUser($input: UpdateUserInput!) {
          updateUser(input: $input) {
            id email name employeeId department managerEmail role shift dateOfJoining createdAt updatedAt
          }
        }
      `;
      const input = { id: userId, ...updates };
      const response = await getClient().graphql({
        query,
        variables: { input }
      }) as GraphQLResponse<{ updateUser: User }>;

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      if (!response.data?.updateUser) {
        throw new Error('Failed to update user profile');
      }

      return response.data.updateUser;
    } catch (error) {
      console.error('Failed to update user profile:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to update user profile: ${error.message}` 
          : 'Failed to update user profile'
      );
    }
  }

  static async createUser(userData: CreateUserInput): Promise<User> {
    try {
      const query = `
        mutation CreateUser($input: CreateUserInput!) {
          createUser(input: $input) {
            id email name employeeId department managerEmail role shift dateOfJoining createdAt updatedAt
          }
        }
      `;
      const response = await getClient().graphql({
        query,
        variables: { input: userData }
      }) as GraphQLResponse<{ createUser: User }>;

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      if (!response.data?.createUser) {
        throw new Error('Failed to create user');
      }

      return response.data.createUser;
    } catch (error) {
      console.error('Failed to create user:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to create user: ${error.message}` 
          : 'Failed to create user'
      );
    }
  }

  static async deactivateUser(userId: string): Promise<{ success: boolean; message: string }> {
    try {
      const query = `
        mutation DeactivateUser($userId: ID!) {
          deactivateUser(userId: $userId) {
            success message
          }
        }
      `;
      const response = await getClient().graphql({
        query,
        variables: { userId }
      }) as GraphQLResponse<{ deactivateUser: { success: boolean; message: string } }>;

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      return response.data?.deactivateUser || { success: false, message: 'Unknown error' };
    } catch (error) {
      console.error('Failed to deactivate user:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to deactivate user: ${error.message}` 
          : 'Failed to deactivate user'
      );
    }
  }

  static async finalApproveExpense(expenseId: string, comments?: string): Promise<Expense> {
    // Use GraphQLExpenseService.approveExpense instead of custom mutation
    // This will update status from MANAGER_APPROVED to FINANCE_APPROVED
    const { GraphQLExpenseService } = await import('./graphqlExpense');
    return GraphQLExpenseService.approveExpense(expenseId, 'finance-admin', comments);
  }

  static async markExpenseReimbursed(expenseId: string, details?: {
    method?: string;
    transactionId?: string;
    notes?: string;
  }): Promise<Expense> {
    // Use GraphQLExpenseService.markReimbursed instead of custom mutation
    const { GraphQLExpenseService } = await import('./graphqlExpense');
    return GraphQLExpenseService.markReimbursed(expenseId);
  }
}
